import React, { useEffect, useState } from "react";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
  PhoneIcon,
  MapPinIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import AddCustomer from "./AddCustomer";
import { DeletecustomerbyId, getallcustomer } from "../../../api/api";
import { ScaleLoader } from "react-spinners";
import toast from "react-hot-toast";

/* ══════════════════════════════════════════
   CUSTOMER DETAIL SIDE PANEL
══════════════════════════════════════════ */
const CustomerPanel = ({ customer, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex justify-end"
      style={{ background: "rgba(15,23,42,0.4)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm h-full bg-white shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-widest text-gray-500 uppercase mb-1">
              Customer Profile
            </p>
            <h2 className="text-lg font-extrabold text-gray-900">{customer.companyName}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{customer.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Logo */}
          {customer.companyPhoto && (
            <div className="flex justify-center">
              <img
                src={customer.companyPhoto}
                alt="Company"
                className="h-20 w-20 rounded-2xl object-cover border border-gray-100"
              />
            </div>
          )}

          {/* Info rows */}
          {[
            { icon: <PhoneIcon className="w-4 h-4" />, label: "Contact", value: customer.contactNo },
            { icon: <MapPinIcon className="w-4 h-4" />, label: "Address", value: customer.address },
            { icon: <IdentificationIcon className="w-4 h-4" />, label: "GST No", value: customer.gstNo || "—" },
          ].map(({ icon, label, value }) => (
            <div key={label} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 flex-shrink-0">
                {icon}
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
                <p className="text-sm font-semibold text-gray-800">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

/* ══════════════════════════════════════════
   MOBILE CUSTOMER CARD
══════════════════════════════════════════ */
const MobileCard = ({ customer, index, currentPage, rowsPerPage, onView, onEdit, onDelete }) => {
  const initials = customer.companyName
    ?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      <div className="h-1 bg-gray-900" />
      <div className="p-4">
        {/* Top */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
              {customer.companyPhoto
                ? <img src={customer.companyPhoto} alt="" className="h-10 w-10 object-cover" />
                : initials}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] font-bold text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                  #{(currentPage - 1) * rowsPerPage + index + 1}
                </span>
              </div>
              <h3 className="font-extrabold text-gray-900 text-sm leading-tight truncate">{customer.companyName}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{customer.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => onEdit(customer)}
              className="w-8 h-8 rounded-xl border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-500 transition-colors"
            >
              <PencilIcon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onDelete(customer)}
              className="w-8 h-8 rounded-xl border border-red-100 hover:bg-red-50 flex items-center justify-center text-red-400 transition-colors"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Data grid */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 bg-gray-50 rounded-xl p-3 mb-3 text-xs">
          <div>
            <p className="text-gray-400 mb-0.5">Contact No</p>
            <p className="font-semibold text-gray-700">{customer.contactNo}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">GST No</p>
            <p className="font-semibold text-gray-700 font-mono">{customer.gstNo || "—"}</p>
          </div>
          <div className="col-span-2">
            <p className="text-gray-400 mb-0.5">Address</p>
            <p className="font-semibold text-gray-700">{customer.address}</p>
          </div>
        </div>

        {/* Footer */}
        <button
          onClick={() => onView(customer)}
          className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl py-2.5 transition-colors"
        >
          <EyeIcon className="w-3.5 h-3.5" /> View Details
        </button>
      </div>
    </motion.div>
  );
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const CustomerTable = () => {
  const [activeTab, setActiveTab]           = useState("All");
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [customers, setCustomers]           = useState([]);
  const [loading, setLoading]               = useState(true);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [isEdit, setIsEdit]                 = useState(false);
  const [isView, setIsView]                 = useState(false);
  const [panelCustomer, setPanelCustomer]   = useState(null);
  const [searchTerm, setSearchTerm]         = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage]       = useState(1);
  const [totalPages, setTotalPages]         = useState(1);
  const [confirmDelete, setConfirmDelete]   = useState(null);
  const rowsPerPage = 5;

  const tabs = ["All", "With GST", "Without GST"];

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const data = await getallcustomer(currentPage, rowsPerPage, debouncedSearch);
      setCustomers(data.data.customers);
      setTotalPages(data.data.totalPages || 1);
    } catch {
      toast.error("Failed to load customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCustomers(); }, [addCustomerOpen, currentPage, debouncedSearch]);

  const filteredCustomers =
    activeTab === "With GST"    ? customers.filter((c) => !!c.gstNo) :
    activeTab === "Without GST" ? customers.filter((c) => !c.gstNo)  :
    customers;

  const handleView   = (c) => { setIsEdit(false); setIsView(true);  setCurrentCustomer(c); setAddCustomerOpen(true); };
  const handleEdit   = (c) => { setIsEdit(true);  setIsView(false); setCurrentCustomer(c); setAddCustomerOpen(true); };
  const handleDelete = (c) => setConfirmDelete(c);

  const confirmDeleteCustomer = async () => {
    if (!confirmDelete) return;
    try {
      await DeletecustomerbyId(confirmDelete._id);
      toast.success(`${confirmDelete.companyName} deleted.`);
      fetchCustomers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setConfirmDelete(null);
    }
  };

  const tabCounts = {
    All:          customers.length,
    "With GST":    customers.filter((c) => !!c.gstNo).length,
    "Without GST": customers.filter((c) => !c.gstNo).length,
  };

  const paginationItems = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push("e-" + p);
      acc.push(p);
      return acc;
    }, []);

  const getInitials = (name = "") =>
    name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  if (addCustomerOpen) {
    return (
      <AddCustomer
        onBack={() => { setAddCustomerOpen(false); setCurrentCustomer(null); setIsView(false); setIsEdit(false); }}
        customer={currentCustomer}
        isEdit={isEdit}
        isView={isView}
      />
    );
  }

  return (
    <>
      <div className="min-h-screen bg-slate-50 p-3 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">

          {/* ── Page Header ── */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight leading-none">
                Customers
              </h1>
              <p className="text-sm text-gray-400 mt-1.5">
                {customers.length} total customer{customers.length !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2.5 shadow-sm flex-1 sm:flex-none sm:w-56 focus-within:ring-2 focus-within:ring-gray-900/10 focus-within:border-gray-400 transition-all">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search customers…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent w-full"
                />
              </div>
              <button
                onClick={() => { setCurrentCustomer(null); setAddCustomerOpen(true); }}
                className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 active:scale-95 text-white text-sm font-bold px-4 py-2.5 rounded-xl shadow-sm transition-all whitespace-nowrap"
              >
                <PlusIcon className="w-4 h-4" />
                Add Customer
              </button>
            </div>
          </div>

          {/* ── Main Card ── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

            {/* ── Tabs ── */}
            <div className="flex overflow-x-auto border-b border-gray-100 scrollbar-none">
              {tabs.map((tab) => {
                const active = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center gap-2 px-4 sm:px-5 py-4 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                      active
                        ? "border-gray-900 text-gray-900"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {tab}
                    <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${
                      active ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500"
                    }`}>
                      {tabCounts[tab]}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ── Loading ── */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <ScaleLoader color="#111827" />
              </div>

            /* ── Empty ── */
            ) : filteredCustomers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                  <BuildingOffice2Icon className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-sm text-gray-400 font-medium">No customers found</p>
                <p className="text-xs text-gray-300">Try adjusting your search or tab</p>
              </div>

            ) : (
              <>
                {/* ════════════════════════════
                    DESKTOP TABLE (md+)
                ════════════════════════════ */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-gray-100">
                        {["#", "Company / Contact", "Phone", "Address", "Logo", "GST No", "Actions"].map((h) => (
                          <th key={h} className="px-5 py-3.5 text-left text-[10px] font-bold tracking-widest text-gray-400 uppercase whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {filteredCustomers.map((customer, index) => (
                        <motion.tr
                          key={customer._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.04 }}
                          className="hover:bg-slate-50/80 transition-colors group"
                        >
                          {/* # */}
                          <td className="px-5 py-4 w-10">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gray-100 text-[10px] font-bold text-gray-500">
                              {(currentPage - 1) * rowsPerPage + index + 1}
                            </span>
                          </td>

                          {/* Company / Contact */}
                          <td className="px-5 py-4 max-w-[200px]">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-lg bg-gray-900 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                                {/* {customer.companyPhoto
                                  ? <img src={customer.companyPhoto} alt="" className="h-8 w-8 object-cover" /> */}
                                  { getInitials(customer.companyName)}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-900 truncate text-[13px]">{customer.companyName}</p>
                                <p className="text-xs text-gray-400 mt-0.5 truncate">{customer.name}</p>
                              </div>
                            </div>
                          </td>

                          {/* Phone */}
                          <td className="px-5 py-4 text-xs text-gray-600 font-mono whitespace-nowrap">
                            {customer.contactNo}
                          </td>

                          {/* Address */}
                          <td className="px-5 py-4 max-w-[160px]">
                            <p className="text-xs text-gray-500 truncate">{customer.address}</p>
                          </td>

                          {/* Logo */}
                          <td className="px-5 py-4">
                            {customer.companyPhoto ? (
                              <img
                                src={customer.companyPhoto}
                                alt="Logo"
                                className="h-7 w-7 object-cover rounded-md border border-gray-100"
                              />
                            ) : (
                              <div className="h-7 w-7 rounded-md border border-dashed border-gray-200 flex items-center justify-center">
                                <BuildingOffice2Icon className="w-3.5 h-3.5 text-gray-300" />
                              </div>
                            )}
                          </td>

                          {/* GST No */}
                          <td className="px-5 py-4">
                            {customer.gstNo ? (
                              <span className="text-xs font-mono text-gray-700 font-semibold">{customer.gstNo}</span>
                            ) : (
                              <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                No GST
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => setPanelCustomer(customer)}
                                className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
                                title="Quick View"
                              >
                                <EyeIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleView(customer)}
                                className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
                                title="Full View"
                              >
                                <IdentificationIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleEdit(customer)}
                                className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
                                title="Edit"
                              >
                                <PencilIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDelete(customer)}
                                className="w-8 h-8 rounded-lg border border-red-100 hover:bg-red-50 flex items-center justify-center text-red-400 hover:text-red-600 transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ════════════════════════════
                    MOBILE CARDS (< md)
                ════════════════════════════ */}
                <div className="md:hidden p-3 space-y-3">
                  {filteredCustomers.map((customer, index) => (
                    <MobileCard
                      key={customer._id}
                      customer={customer}
                      index={index}
                      currentPage={currentPage}
                      rowsPerPage={rowsPerPage}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </>
            )}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-gray-400 hidden sm:block">
                  Page <span className="font-semibold text-gray-700">{currentPage}</span> of{" "}
                  <span className="font-semibold text-gray-700">{totalPages}</span>
                </p>
                <div className="flex items-center gap-1.5 mx-auto sm:mx-0">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronLeftIcon className="w-3.5 h-3.5" />
                  </button>

                  {paginationItems.map((item) =>
                    typeof item === "string" ? (
                      <span key={item} className="text-gray-400 text-xs px-0.5">···</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all shadow-sm ${
                          currentPage === item
                            ? "bg-gray-900 text-white border border-gray-900"
                            : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        {item}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 transition-colors shadow-sm"
                  >
                    <ChevronRightIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Quick View Side Panel ── */}
      {panelCustomer && (
        <CustomerPanel
          customer={panelCustomer}
          onClose={() => setPanelCustomer(null)}
        />
      )}

      {/* ── Delete Confirm Dialog ── */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: "rgba(15,23,42,0.4)", backdropFilter: "blur(3px)" }}
            onClick={() => setConfirmDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm"
            >
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                <TrashIcon className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-base font-extrabold text-gray-900 mb-1">Delete Customer</h3>
              <p className="text-sm text-gray-400 mb-6">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-gray-700">{confirmDelete?.companyName}</span>?
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteCustomer}
                  className="flex-1 py-2.5 rounded-xl bg-gray-900 text-sm font-bold text-white hover:bg-gray-800 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomerTable;